import curry from "../../lib/core/functional/curry";
import { updateSiteInfo, getSiteInfo } from "../../transport/site";

const userTransports = {};
const transportWaiters = {};

function didChanged(newProfile, fullProfile) {
    return Boolean(
        Object.entries(newProfile).find(function isDif([key, value]) {
            return fullProfile[key] !== value;
        })
    );
}

function makeSelfEditTransport(context) {
    let fullProfile;
    const changeListeners = [];

    function emitProfile(callback) {
        callback(fullProfile);
    }

    function subscribeToChanges(callback) {
        changeListeners.push(callback);
        if (fullProfile) {
            callback(fullProfile);
        }
    }

    function onContextInfoChange(err, userInfo) {
        if (err) {
            return;
        }

        const { profileSite } = userInfo;
        fullProfile = { ...profileSite };
        changeListeners.forEach(emitProfile);
    }

    function sendChanges(changes) {
        if (didChanged(changes, fullProfile)) {
            const newBody = { ...fullProfile, ...changes };
            updateSiteInfo(fullProfile.id, newBody).then(context.refreshUserInfo);
        }
    }

    context.listenForUserInfo(onContextInfoChange);

    return {
        subscribeToChanges,
        sendChanges,
    };
}

function makeSiteEditTransport(siteData) {
    let fullProfile = { ...siteData };
    const changeListeners = [];

    function emitProfile(callback) {
        callback(fullProfile);
    }

    function subscribeToChanges(callback) {
        changeListeners.push(callback);
        callback(fullProfile);
    }

    function siteChanged(newData) {
        fullProfile = { ...newData };
        changeListeners.forEach(emitProfile);
    }

    function sendChanges(changes) {
        if (didChanged(changes, fullProfile)) {
            const newBody = { ...fullProfile, ...changes };
            updateSiteInfo(fullProfile.id, newBody).then(siteChanged);
        }
    }

    return {
        subscribeToChanges,
        sendChanges,
    };
}

function onSiteInfo(siteData) {
    if (!siteData.permissions.edit_site) {
        throw new Error("You have no permissions to edit this site");
    }

    return makeSiteEditTransport(siteData);
}

const onUserInfo = curry(function onUserInfo(context, siteId, userInfo) {
    const { runnerSite, profileSite } = userInfo;

    if (
        siteId === profileSite.id &&
        (profileSite.permissions.edit_site || runnerSite.permissions.edit_site)
    ) {
        return makeSelfEditTransport(context);
    }

    return getSiteInfo(siteId).then(onSiteInfo);
});

const cacheDataLayer = curry(function cacheDataLayer(siteId, userTransport) {
    userTransports[siteId] = userTransport;
    return userTransport;
});

const resolvePromises = curry(function resolvePromises(siteId, userTransport) {
    transportWaiters[siteId].forEach(([resolve]) => {
        resolve(userTransport);
    });
});

const rejectPromises = curry(function rejectPromises(siteId, error) {
    transportWaiters[siteId].forEach(([_, reject]) => {
        reject(error);
    });
});

export function getUserTransport(context, siteId) {
    if (siteId in userTransports) {
        return Promise.resolve(userTransports[siteId]);
    }

    return new Promise((resolve, reject) => {
        const promise = [resolve, reject];
        if (siteId in transportWaiters) {
            transportWaiters[siteId].push(promise);
        } else {
            transportWaiters[siteId] = [promise];
            context
                .getUserInfo()
                .then(onUserInfo(context, siteId))
                .then(cacheDataLayer(siteId))
                .then(resolvePromises(siteId))
                .catch(rejectPromises(siteId));
        }
    });
}

export function getSiteId(context, element) {
    const parentSiteIdElement = element.closest("[data-edit-site-id]");

    if (parentSiteIdElement) {
        return parseInt(parentSiteIdElement.dataset.editSiteId, 10);
    }

    const {
        settings: { site },
    } = context;

    if (site.isCommunitySite) {
        return site.id;
    }
}
